import {
  LocationReferenceType,
  SubmissionChannel,
} from "@amzn/geo-edit-management-service-typescript-client";
import {
  fullStoreToGemEdits,
  useAllForms,
  useCreateSuggestedEdit,
  useEntityId,
  useTerminate,
} from "@amzn/geo-studio-web-sdk";
import { Box, Button, Column, Divider, Loader, Modal, Text } from "@amzn/meridian";
import { useEffect } from "react";

type ChangePreviewModalProps = {
  isChangePreviewModalOpen: boolean;
  setIsChangePreviewModalOpen: (state: false) => void;
};

const ChangePreviewModal = (props: ChangePreviewModalProps) => {
  const { allForms, allFormValidities, resetAllForms } = useAllForms();
  const [selectedEntity, setEntityId] = useEntityId();
  const terminate = useTerminate();

  const onClearChanges = () => {
    resetAllForms();
    setEntityId(undefined);
  };

  const onError = (error: Error) => {
    console.error(error);
  };

  const {
    mutate: sendSuggestedEdit,
    reset: resetMutation,
    isSuccess,
    isPending,
    isError,
    error: gemsError,
    data: gemsResponseData,
  } = useCreateSuggestedEdit(() => {}, onError);

  // Reset mutation state when modal opens to clear stale success/error from previous submission
  useEffect(() => {
    if (props.isChangePreviewModalOpen) {
      resetMutation();
    }
  }, [props.isChangePreviewModalOpen, resetMutation]);

  const canSubmit =
    Object.keys(allForms).length > 0 && Object.keys(allForms).every((id) => allFormValidities[id]);

  // TODO: Change submission logic once ready
  const submitChanges = () => {
    const confirmed = confirm(`Submitting all changes: ${Object.keys(allForms).length} entities`);
    if (confirmed) {
      sendSuggestedEdit({
        submissionChannel: SubmissionChannel.WIDGET,
        attributeChanges: fullStoreToGemEdits(allForms),
        /**
         * TODO: catch up with GEMS team on what they expect for location reference to be:
         *
         * 1 - the location of the place from where the edit is being made
         * 2 - the location related to the place that is being edited
         *       - note that case 2 conflicts internally with the definition of the model
         */
        locationReference: {
          // for now -- assume AID is being used
          locationReferenceType: LocationReferenceType.ADDRESS,
          locationReferenceId: selectedEntity,
        },
      });
    }
  };

  return (
    <Modal
      title="Preview changes"
      open={props.isChangePreviewModalOpen}
      onClose={() => props.setIsChangePreviewModalOpen(false)}
    >
      <Column>
        <Button onClick={submitChanges} disabled={!canSubmit}>
          Submit changes
        </Button>

        {isPending ? (
          <Loader data-testid="loader" />
        ) : isError ? (
          <>
            <Text data-testid="gems-error-text">Gems submission failed.</Text>
            <Text data-testid="gems-api-error-message">{gemsError.message}</Text>
            <Button
              onClick={() =>
                terminate({
                  reason: "USER_ACTION",
                  source: "Sample-Component",
                  humanReadableTrace: "User pressed close after GEMS submission failure",
                })
              }
              data-testid="close-button"
            >
              Close
            </Button>
          </>
        ) : isSuccess ? (
          <>
            <Text data-testid="submission-success-text">Submission success!</Text>
            <Text data-testid="gems-edit-id">{`Gems edit id: ${gemsResponseData.editId}`}</Text>
            <Button
              onClick={() =>
                terminate({
                  reason: "USER_ACTION",
                  source: "Sample-Component",
                  humanReadableTrace: "User pressed close after GEMS submission success",
                })
              }
              data-testid="close-button"
            >
              Close
            </Button>
          </>
        ) : (
          <></>
        )}
        {Object.entries(allForms).map(([entityId, attributes]) => (
          <Box key={entityId} spacingInset="300" data-testid={`entity-${entityId}`}>
            <Text type="h200">
              Entity ID: {entityId} — {allFormValidities[entityId] ? "Valid" : "⚠️ Invalid"}
            </Text>
            {Object.entries(attributes).map(([key, value]) => (
              <Box key={key} spacingInset="200 none" data-testid={`attribute-${entityId}-${key}`}>
                <Text>Attribute: {key}</Text>
                <Text type="b200">
                  <pre style={{ margin: 0 }}>
                    {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
                  </pre>
                </Text>
              </Box>
            ))}
            <Divider />
          </Box>
        ))}
        {!isPending && canSubmit ? (
          <Button onClick={onClearChanges} disabled={!canSubmit}>
            Clear changes
          </Button>
        ) : (
          <></>
        )}
      </Column>
    </Modal>
  );
};

export default ChangePreviewModal;
